import { nextTick, type ComponentPublicInstance } from 'vue';

const FOCUSABLE_SELECTOR =
  'input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])';

export function focusElement(el: HTMLElement | null | undefined): boolean {
  if (!el) return false;
  el.focus();
  if (typeof document === 'undefined') return true;
  const active = document.activeElement;
  return active === el || (active instanceof Node && el.contains(active));
}

export function focusById(id: string): boolean {
  const el = document.getElementById(id);
  if (!(el instanceof HTMLElement)) return false;
  return focusElement(el);
}

export function findFirstFocusable(
  root: HTMLElement | null | undefined,
): HTMLElement | null {
  if (!root) return null;
  return root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
}

export function focusPrimeVueInput(
  componentRef:
    | ComponentPublicInstance
    | { $el?: HTMLElement }
    | null
    | undefined,
): boolean {
  const root = componentRef?.$el;
  if (
    root instanceof HTMLInputElement ||
    root instanceof HTMLTextAreaElement ||
    root instanceof HTMLSelectElement ||
    root instanceof HTMLButtonElement
  ) {
    return focusElement(root);
  }
  if (!(root instanceof HTMLElement)) return false;
  return focusElement(findFirstFocusable(root));
}

export function focusWithin(
  root: HTMLElement | null | undefined,
): boolean {
  return focusElement(findFirstFocusable(root));
}

export async function yieldFocusPaint(): Promise<void> {
  await nextTick();
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

export async function focusWithRetry(
  focus: () => boolean,
  maxAttempts = 10,
): Promise<boolean> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await yieldFocusPaint();
    if (focus()) return true;
  }
  return false;
}
